// =============================================================================
// ENTITY NODE - Custom React Flow node for data entities
// =============================================================================

import { memo, useState } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import {
  User,
  Building2,
  FileText,
  Package,
  ArrowRightLeft,
  GitBranch,
  Calendar,
  MapPin,
  ChevronDown,
  ChevronUp,
  Key,
  Hash,
  Trash2,
  Copy,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../../../../lib/utils';
import type { Entity, EntityField, PrimitiveType } from '../../types';

const PRIMITIVE_ICONS: Record<PrimitiveType, LucideIcon> = {
  person: User,
  organization: Building2,
  document: FileText,
  asset: Package,
  transaction: ArrowRightLeft,
  workflow: GitBranch,
  event: Calendar,
  location: MapPin,
};

const FIELD_TYPE_ICONS: Record<string, LucideIcon> = {
  string: Hash,
  number: Hash,
  boolean: Hash,
  date: Calendar,
  datetime: Calendar,
  email: User,
  phone: User,
  url: Hash,
  json: Hash,
  file: FileText,
  enum: Hash,
  relation: ArrowRightLeft,
};

interface EntityNodeData extends Entity {
  onDelete?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  onFieldClick?: (entityId: string, fieldId: string) => void;
}

function EntityNodeComponent({ data, selected }: NodeProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const entity = data as unknown as EntityNodeData;
  const Icon = PRIMITIVE_ICONS[entity.primitive] || Package;
  const color = entity.color || '#6B7280';

  // Separate primitive fields from custom fields
  const primitiveFields = entity.fields.filter((f) => f.isPrimitive);
  const customFields = entity.fields.filter((f) => !f.isPrimitive);

  return (
    <>
      {/* Input handles on top - for incoming relations */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-secondary-400 !border-2 !border-white dark:!border-secondary-800"
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        className="!w-3 !h-3 !bg-secondary-400 !border-2 !border-white dark:!border-secondary-800"
      />

      <div
        className={cn(
          'min-w-[280px] rounded-xl border-2 bg-white shadow-lg transition-all',
          'dark:bg-secondary-800',
          selected
            ? 'shadow-xl ring-4'
            : 'border-secondary-200 dark:border-secondary-700 hover:shadow-xl'
        )}
        style={{
          borderColor: selected ? color : undefined,
          ['--tw-ring-color' as string]: selected ? `${color}30` : undefined,
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-3 rounded-t-xl cursor-pointer"
          style={{ backgroundColor: `${color}15` }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-lg shadow-md"
              style={{ backgroundColor: color }}
            >
              <Icon className="h-5 w-5 text-white" />
            </div>
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-secondary-500">
                {entity.primitive}
              </span>
              <p className="font-bold text-secondary-900 dark:text-white">
                {entity.name}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {entity.onDuplicate && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  entity.onDuplicate?.(entity.id);
                }}
                className="p-1.5 rounded-md hover:bg-secondary-200 dark:hover:bg-secondary-700 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300"
              >
                <Copy className="h-4 w-4" />
              </button>
            )}
            {entity.onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  entity.onDelete?.(entity.id);
                }}
                className="p-1.5 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 text-secondary-400 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-secondary-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-secondary-400" />
            )}
          </div>
        </div>

        {/* Fields */}
        {isExpanded && (
          <div className="border-t border-secondary-100 dark:border-secondary-700">
            {/* Primitive fields (from template) */}
            {primitiveFields.length > 0 && (
              <div className="p-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-secondary-400 px-2 mb-1">
                  Primitive Fields
                </p>
                {primitiveFields.map((field) => (
                  <FieldRow
                    key={field.id}
                    field={field}
                    color={color}
                    onClick={() => entity.onFieldClick?.(entity.id, field.id)}
                  />
                ))}
              </div>
            )}

            {/* Custom fields */}
            {customFields.length > 0 && (
              <div className="p-2 border-t border-secondary-100 dark:border-secondary-700">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-secondary-400 px-2 mb-1">
                  Custom Fields
                </p>
                {customFields.map((field) => (
                  <FieldRow
                    key={field.id}
                    field={field}
                    color={color}
                    onClick={() => entity.onFieldClick?.(entity.id, field.id)}
                  />
                ))}
              </div>
            )}

            {/* Empty state */}
            {entity.fields.length === 0 && (
              <div className="p-4 text-center text-secondary-400 text-sm">
                No fields defined
              </div>
            )}
          </div>
        )}

        {/* Footer with field count */}
        <div className="flex items-center justify-between px-3 py-2 bg-secondary-50 dark:bg-secondary-900/50 rounded-b-xl border-t border-secondary-100 dark:border-secondary-700">
          <span className="text-xs text-secondary-500">
            {entity.fields.length} field{entity.fields.length !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-1">
            {entity.fields.some((f) => f.required) && (
              <span className="text-[10px] bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded">
                required
              </span>
            )}
            {entity.fields.some((f) => f.unique) && (
              <span className="text-[10px] bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-1.5 py-0.5 rounded">
                unique
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Output handles - for outgoing relations */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-primary-500 !border-2 !border-white dark:!border-secondary-800"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        className="!w-3 !h-3 !bg-primary-500 !border-2 !border-white dark:!border-secondary-800"
      />
    </>
  );
}

// Field row component
function FieldRow({
  field,
  onClick,
}: {
  field: EntityField;
  color?: string;
  onClick?: () => void;
}) {
  const Icon = FIELD_TYPE_ICONS[field.type] || Hash;

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer',
        'hover:bg-secondary-100 dark:hover:bg-secondary-700/50 transition-colors'
      )}
      onClick={onClick}
    >
      <Icon className="h-3.5 w-3.5 text-secondary-400 flex-shrink-0" />
      <span className="text-sm text-secondary-700 dark:text-secondary-300 truncate flex-1">
        {field.name}
      </span>
      <span className="text-xs text-secondary-400">{field.type}</span>
      {field.required && (
        <span className="text-red-500 text-xs">*</span>
      )}
      {field.unique && (
        <Key className="h-3 w-3 text-purple-500" />
      )}
    </div>
  );
}

export const EntityNode = memo(EntityNodeComponent);
